import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle,
} from '@mui/material'
import type { ApiCustomViewNote } from '@/types/api'

interface NoteConflictDialogProps {
  /** The editor buffer that lost the race. */
  mine: string
  /** The note that won, as the server returned it on the 409. Null when the other side deleted it. */
  theirs: ApiCustomViewNote | null
  saving: boolean
  onCancel: () => void
  onResolve: (body: string, rowVersion: string | null) => void
  /** Drops the losing buffer and adopts the winning note as-is. No write: it is already stored. */
  onTakeTheirs: (theirs: ApiCustomViewNote) => void
}

const MINE_ID = 'note-conflict-mine'
const EDITOR_ID = 'note-conflict-editor'

/**
 * Shown when a shared-view note was changed underneath the editor. The stored version is what
 * gets edited and saved, with the user's own text shown read-only above it for reference, so
 * nothing already committed by a colleague can be dropped without the user retyping over it.
 *
 * The caller keys this on the winning token, so a second collaborator landing another edit while
 * the dialog is open remounts it with their text instead of leaving a stale copy on screen.
 */
export function NoteConflictDialog({
  mine, theirs, saving, onCancel, onResolve, onTakeTheirs,
}: NoteConflictDialogProps) {
  const { t } = useTranslation()
  const deleted = theirs === null

  // A deleted note has no stored version to build on, so the user's own text is the starting point.
  const [body, setBody] = useState(theirs?.body ?? mine)

  return (
    <Dialog open onClose={saving ? undefined : onCancel} maxWidth="md" fullWidth>
      <DialogTitle>{t('notes.conflictTitle')}</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          {deleted
            ? t('notes.conflictDeleted')
            : t('notes.conflictNewerVersion', { name: theirs.updatedBy ?? t('notes.conflictSomeone') })}
        </Alert>
        {!deleted && (
          <>
            <label className="note-conflict-label" htmlFor={MINE_ID}>{t('notes.conflictMineReadOnlyLabel')}</label>
            <textarea
              id={MINE_ID}
              className="note-conflict-textarea note-conflict-textarea--readonly"
              value={mine}
              readOnly
            />
          </>
        )}
        <label className="note-conflict-label" htmlFor={EDITOR_ID}>
          {deleted ? t('notes.conflictMineLabel') : t('notes.conflictTheirsLabel')}
        </label>
        <p className="note-conflict-hint">{t('notes.conflictEditHint')}</p>
        <textarea
          id={EDITOR_ID}
          className="note-conflict-textarea"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          autoFocus
        />
      </DialogContent>
      <DialogActions className="note-conflict-actions">
        <Button onClick={onCancel} disabled={saving}>{t('notes.conflictCancel')}</Button>
        {/* Both sides resolve in one click: theirs is already the stored note, so it only replaces
            the buffer, while mine is written against the winning token like any other save. */}
        {theirs !== null && (
          <Button onClick={() => onTakeTheirs(theirs)} disabled={saving}>
            {t('notes.conflictTakeTheirs')}
          </Button>
        )}
        <Button
          onClick={() => onResolve(mine, theirs?.rowVersion ?? null)}
          disabled={saving || !mine.trim()}
        >
          {t('notes.conflictKeepMine')}
        </Button>
        {/* Saves with the token from the copy that won, so this write is itself checked and a
            third editor arriving mid-dialog cannot be overwritten either. */}
        <Button
          variant="contained"
          onClick={() => onResolve(body, theirs?.rowVersion ?? null)}
          disabled={saving || !body.trim()}
        >
          {saving ? t('notes.saving') : t('notes.conflictSave')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
